/**
 * deckWriteback.ts — applies review decisions back to the live PowerPoint deck
 * via the Office.js PowerPoint API.
 *
 * Compare Slides is otherwise read-only (it parses bytes and diffs off-thread);
 * this module is the *only* place that mutates the open presentation, so the
 * write surface stays small and auditable:
 *   - `setShapeText` — replace a shape's text (revert → original, keep → modified)
 *   - `deleteShapeByName` — drop a shape (revert of an added shape)
 *   - `deleteSlideAt` — drop a slide (revert of an added slide)
 *   - `insertSlideFromBase64` — re-add a slide (revert of a removed slide)
 *
 * Shapes are located by the same name the extractor read from the OOXML
 * (`p:cNvPr@name`), which matches `Shape.name` in the PowerPoint API.
 */

/** Load the slides collection and return the slide at `slideIndex`, or throw. */
async function requireSlide(context: PowerPoint.RequestContext, slideIndex: number): Promise<PowerPoint.Slide> {
  const slides = context.presentation.slides;
  slides.load("items");
  await context.sync();

  const slide = slides.items[slideIndex];
  if (!slide) throw new Error(`Slide ${slideIndex + 1} was not found in the current deck.`);
  return slide;
}

/** Load a slide's shapes by name and return the one matching `shapeName` (or undefined). */
async function findShapeByName(
  context: PowerPoint.RequestContext,
  slide: PowerPoint.Slide,
  shapeName: string,
): Promise<PowerPoint.Shape | undefined> {
  const shapes = slide.shapes;
  shapes.load("items/name");
  await context.sync();
  return shapes.items.find((s) => s.name === shapeName);
}

/** Replace the whole text of a named shape on a slide (0-based slide index). */
export async function setShapeText(slideIndex: number, shapeName: string, text: string): Promise<void> {
  await PowerPoint.run(async (context) => {
    const slide = await requireSlide(context, slideIndex);
    const shape = await findShapeByName(context, slide, shapeName);
    if (!shape) throw new Error(`Shape "${shapeName}" was not found on slide ${slideIndex + 1}.`);

    shape.textFrame.textRange.text = text;
    await context.sync();
  });
}

/**
 * Delete a named shape from a slide (revert of an added shape). Missing shape is
 * a no-op — reverting a shape that's already gone should not error.
 */
export async function deleteShapeByName(slideIndex: number, shapeName: string): Promise<void> {
  await PowerPoint.run(async (context) => {
    const slide = await requireSlide(context, slideIndex);
    const shape = await findShapeByName(context, slide, shapeName);
    if (!shape) return; // already removed — nothing to do

    shape.delete();
    await context.sync();
  });
}

/** Delete the slide at the given 0-based index (revert of an added slide). */
export async function deleteSlideAt(slideIndex: number): Promise<void> {
  await PowerPoint.run(async (context) => {
    const slide = await requireSlide(context, slideIndex);
    slide.delete();
    await context.sync();
  });
}

/**
 * Insert the slide(s) contained in a base64 `.pptx` package into the live deck.
 * `afterSlideIndex` is the 0-based index of the slide to insert *after*; pass
 * `null` to insert at the beginning. Used to re-add a removed slide, where the
 * base64 is a single-slide package built from the original deck.
 */
export async function insertSlideFromBase64(base64: string, afterSlideIndex: number | null): Promise<void> {
  await PowerPoint.run(async (context) => {
    const options: PowerPoint.InsertSlideOptions = {
      formatting: PowerPoint.InsertSlideFormatting.keepSourceFormatting,
    };

    if (afterSlideIndex != null) {
      const slides = context.presentation.slides;
      slides.load("items/id");
      await context.sync();
      const target = slides.items[afterSlideIndex];
      if (target) options.targetSlideId = target.id;
    }

    context.presentation.insertSlidesFromBase64(base64, options);
    await context.sync();
  });
}
